import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ESTADOS_ENVIO, ESTADO_ENVIO_LABEL, ESTADO_ENVIO_COLOR, type EstadoEnvio } from '@/lib/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Dropdown del estado de envío manual del booking (Origin/In Transit/Arrived/Closed).
 * Llama a PATCH /bookings/:id/estado-envio. La API valida el permiso (agente dueño /
 * delegado por vacaciones / gerente supervisor / admin); si no, responde 403.
 * Va dentro del header clickeable del booking → stopPropagation para no togglear.
 */
export function EstadoEnvioSelector({
  bookingId,
  value,
  compacto = false,
}: {
  bookingId: number;
  value: EstadoEnvio;
  /** true = sin flecha y ancho ajustado al contenido (tabla de Operaciones). */
  compacto?: boolean;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();

  const mut = useMutation({
    mutationFn: (estadoEnvio: EstadoEnvio) =>
      api<{ id: number; estadoEnvio: EstadoEnvio }>(`/bookings/${bookingId}/estado-envio`, {
        method: 'PATCH',
        body: JSON.stringify({ estadoEnvio }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['reporte-operaciones'] });
      qc.invalidateQueries({ queryKey: ['estado-envio-counts'] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo cambiar el estado de envío');
    },
  });

  // supervisor y ventas son solo-lectura: muestran el estado sin poder cambiarlo.
  if (user?.rol === 'supervisor' || user?.rol === 'ventas') {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <span
          className="inline-block h-7 w-32 px-2 leading-7 text-xs rounded border border-ink-200 truncate font-medium"
          style={{ backgroundColor: ESTADO_ENVIO_COLOR[value].bg, color: ESTADO_ENVIO_COLOR[value].text }}
          title="Estado de envío"
        >
          {ESTADO_ENVIO_LABEL[value]}
        </span>
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        value={value}
        onValueChange={(v) => mut.mutate(v as EstadoEnvio)}
        disabled={mut.isPending}
      >
        <SelectTrigger
          className={
            compacto
              ? 'h-7 w-auto min-w-0 px-2 text-xs font-medium border-ink-200 whitespace-nowrap [&_svg]:hidden gap-0'
              : 'h-7 w-32 text-xs font-medium border-ink-200'
          }
          style={{ backgroundColor: ESTADO_ENVIO_COLOR[value].bg, color: ESTADO_ENVIO_COLOR[value].text }}
          title="Estado de envío (click para cambiar)"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ESTADOS_ENVIO.map((e) => (
            // Closed es automático (CW CMP/CLS): no se puede elegir a mano
            <SelectItem
              key={e}
              value={e}
              disabled={e === 'closed' && value !== 'closed'}
              title={e === 'closed' ? 'Se asigna automáticamente cuando CW marca CMP/CLS' : undefined}
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm border border-ink-200"
                  style={{ backgroundColor: ESTADO_ENVIO_COLOR[e].bg }}
                />
                {ESTADO_ENVIO_LABEL[e]}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
